/*
 * Shopify Forms integration for the recipe page and product recipe popup.
 *
 * Shopify Forms are rendered inside a Shadow DOM, so regular theme styles
 * cannot reach the form elements directly.
 *
 * The CSS asset URLs are JSON-encoded in Liquid and exposed to JavaScript
 * via window.shopifyFormCssUrl and window.shopifyFormPopupCssUrl.
 * This script waits for the Shopify Forms web components and injects the
 * corresponding stylesheet into each form's Shadow DOM.
 *
 * It also handles the product recipe popup: the Shopify Form is moved into
 * the modal, the product name is prefilled, available Shopify variants are
 * read from the product JSON and added as a select field, and the form is
 * returned to its hidden DOM storage when the popup is closed.
 */

(() => {
  const FORM_IDS = [
    'forms-root-1177127',
    'forms-root-1185109',
    'forms-root-1197503'
  ];

  const LINK_ID = 'liveocare-shopify-form-css';

  function injectStyles(embed) {
    if (!embed?.shadowRoot || !window.shopifyFormCssUrl) return false;

    const root = embed.shadowRoot;

    if (!root.getElementById(LINK_ID)) {
      const link = document.createElement('link');
      link.id = LINK_ID;
      link.rel = 'stylesheet';
      link.href = window.shopifyFormCssUrl;
      root.appendChild(link);
    }

    return true;
  }

  function scan() {
    FORM_IDS.forEach(formId => {
      document
        .querySelectorAll(`[data-forms-id="${formId}"] shopify-forms-embed`)
        .forEach(injectStyles);
    });
  }

  scan();

  new MutationObserver(scan).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  let attempts = 0;

  const interval = setInterval(() => {
    scan();

    if (++attempts >= 80) {
      clearInterval(interval);
    }
  }, 250);

  customElements
    .whenDefined('shopify-forms-embed')
    .then(scan)
    .catch(() => {});

  document.addEventListener('DOMContentLoaded', scan);
})();

/********************** Shopify-Form Design / Produkt-Popup **************/

(() => {
  const FORM_ID = 'forms-root-1186963';
  const LINK_ID = 'liveocare-shopify-form-popup-css';

  function injectStyles(embed) {
    if (!embed?.shadowRoot || !window.shopifyFormPopupCssUrl) return;

    const root = embed.shadowRoot;

    if (root.getElementById(LINK_ID)) return;

    const link = document.createElement('link');
    link.id = LINK_ID;
    link.rel = 'stylesheet';
    link.href = window.shopifyFormPopupCssUrl;

    root.appendChild(link);
  }

  function scan() {
    document
      .querySelectorAll(`[data-forms-id="${FORM_ID}"] shopify-forms-embed`)
      .forEach(injectStyles);
  }

  scan();

  new MutationObserver(scan).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  customElements
    .whenDefined('shopify-forms-embed')
    .then(scan)
    .catch(() => {});

  document.addEventListener('DOMContentLoaded', scan);
})();

/************************ Rezept-Form-Popup *********************/

(() => {
  const storage = document.createElement('div');
  storage.id = 'lc-form-storage';
  storage.hidden = true;
  document.body.appendChild(storage);

  const forms = new Map();

  const getBlock = formId =>
    document.querySelector(`[data-recipe-form-block][data-form-id="${CSS.escape(formId)}"]`);

  const setValue = (field, value) => {
    const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(field), 'value')?.set;
    setter ? setter.call(field, value) : field.value = value;
    field.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    field.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  };

  const getField = (root, type) => {
    const labels = [...root.querySelectorAll('label')];

    const label = labels.find(label => {
      const text = label.textContent.trim().toLowerCase().replace(/\*/g, '');

      if (type === 'size') {
        return text.startsWith('produktgröße') || text.startsWith('produktgroesse');
      }

      return text === 'produkt' || text.startsWith('produkt ');
    });

    if (!label) return null;

    if (label.htmlFor) {
      const field = root.getElementById(label.htmlFor);
      if (field) return field;
    }

    return label.parentElement?.querySelector('input, textarea, select') || null;
  };

  const getVariants = formId => {
    const block = getBlock(formId);
    if (!block) return null;

    const productFormId = block.querySelector('input[form]')?.getAttribute('form');
    if (!productFormId) return null;

    const picker = [...document.querySelectorAll('variant-selects')].find(picker =>
      picker.querySelector(`[form="${CSS.escape(productFormId)}"]`)
    );

    if (!picker) return null;

    let variants = [];

    try {
      variants = JSON.parse(
        picker.querySelector('script[data-product-variants]')?.textContent || '[]'
      );
    } catch {
      return null;
    }

    variants = variants.filter(variant => variant.available !== false);

    const variantId = document.querySelector(
      `#${CSS.escape(productFormId)} [name="id"]`
    )?.value;

    const selected = variants.find(
      variant => String(variant.id) === String(variantId)
    );

    return {
      title: block.dataset.productTitle || '',
      values: variants
        .map(variant => variant.public_title || variant.title)
        .filter(value => value && value !== 'Default Title'),
      selected: selected?.public_title || selected?.title || ''
    };
  };

  const prepareForm = formId => {
    const formRoot = forms.get(formId);
    const embed = formRoot?.querySelector('shopify-forms-embed');

    if (!embed?.shadowRoot) {
      setTimeout(() => prepareForm(formId), 100);
      return;
    }

    const root = embed.shadowRoot;
    const product = getVariants(formId);

    if (!product) return;

    const productField = getField(root, 'product');

    if (productField) {
      setValue(productField, product.title);
      productField.readOnly = true;
    }

    const sizeField = getField(root, 'size');

    if (!sizeField || !product.values.length) return;

    let select = root.querySelector('.lc-recipe-size-select');

    if (!select) {
      select = document.createElement('select');
      select.className = 'lc-recipe-size-select';
      select.style.cssText = 'width:100%;min-height:48px;padding:10px 14px;border:1px solid #dadada;border-radius:6px;background:#fff;font:inherit;';
      sizeField.style.display = 'none';
      sizeField.insertAdjacentElement('afterend', select);

      select.addEventListener('change', () => {
        setValue(sizeField, select.value);
      });
    }

    select.innerHTML =
      '<option value="">Bitte auswählen</option>' +
      [...new Set(product.values)]
        .map(value => `<option value="${value.replace(/"/g, '&quot;')}">${value}</option>`)
        .join('');

    if (product.selected) {
      select.value = product.selected;
      setValue(sizeField, product.selected);
    }
  };

  const storeForms = () => {
    document
      .querySelectorAll('[data-recipe-form-block][data-form-id]')
      .forEach(block => {
        const formId = block.dataset.formId;

        if (forms.has(formId)) return;

        const form = document.querySelector(
          `[data-forms-id="${CSS.escape(formId)}"]`
        );

        if (!form) return;

        forms.set(formId, form);
        storage.appendChild(form);
      });
  };

  const getPopup = formId => {
    let popup = document.querySelector(
      `.lc-form-popup[data-form-id="${CSS.escape(formId)}"]`
    );

    if (popup) return popup;

    popup = document.createElement('div');
    popup.className = 'lc-form-popup';
    popup.dataset.formId = formId;
    popup.setAttribute('aria-hidden', 'true');
    popup.innerHTML = `
      <div class="lc-form-popup__backdrop"></div>
      <div class="lc-form-popup__dialog" role="dialog" aria-modal="true" aria-label="Formular">
        <button type="button" class="lc-form-popup__close" aria-label="Popup schließen">&times;</button>
        <div class="lc-form-popup__content"></div>
      </div>
    `;

    document.body.appendChild(popup);
    return popup;
  };

  const openPopup = (trigger, formId) => {
    storeForms();

    const form = forms.get(formId);
    if (!form) return;

    const popup = getPopup(formId);

    popup.querySelector('.lc-form-popup__content').appendChild(form);
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('form-popup-open');

    prepareForm(formId);
  };

  const closePopup = popup => {
    const formId = popup.dataset.formId;
    const form = forms.get(formId);

    if (form) storage.appendChild(form);

    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('form-popup-open');

    document
      .querySelectorAll(`.js-open-form-popup[data-form-id="${CSS.escape(formId)}"]`)
      .forEach(button => button.setAttribute('aria-expanded', 'false'));
  };

  new MutationObserver(storeForms).observe(document.body, {
    childList: true,
    subtree: true
  });

  storeForms();

  document.addEventListener('click', event => {
    const trigger = event.target.closest('.js-open-form-popup');

    if (trigger) {
      event.preventDefault();
      openPopup(trigger, trigger.dataset.formId);
      return;
    }

    const close = event.target.closest(
      '.lc-form-popup__close, .lc-form-popup__backdrop'
    );

    if (close) closePopup(close.closest('.lc-form-popup'));
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      const popup = document.querySelector('.lc-form-popup.is-open');
      if (popup) closePopup(popup);
    }
  });
})();